import { parseCategory } from "../../model/categories";

/** Why an extension is recommended: the seal's text (`pages.domains.tags.reasons.{reason}`). */
export type RecommendationReason =
  | "store"
  | "artists"
  | "creators"
  | "blogs"
  | "tech"
  | "agencies"
  | "professionals"
  | "studios"
  | "courses"
  | "organizations"
  | "personal";

type Match = { reason: RecommendationReason; tlds: string[] };

const STORE: Match = { reason: "store", tlds: ["store", "shop"] };
const ARTISTS: Match = { reason: "artists", tlds: ["art", "studio", "design"] };
const CREATORS: Match = { reason: "creators", tlds: ["live", "bio"] };
const BLOGS: Match = { reason: "blogs", tlds: ["blog"] };
const TECH: Match = { reason: "tech", tlds: ["tech", "dev", "app"] };
const AGENCIES: Match = { reason: "agencies", tlds: ["agency", "digital"] };
const PROFESSIONALS: Match = { reason: "professionals", tlds: ["pro"] };
const STUDIOS: Match = { reason: "studios", tlds: ["studio"] };
const COURSES: Match = { reason: "courses", tlds: ["digital", "online"] };
const ORGANIZATIONS: Match = { reason: "organizations", tlds: ["org"] };
const PERSONAL: Match = { reason: "personal", tlds: ["me", "bio"] };

/** The extensions that suit a Keepo's field of work (site.category), and why. */
const matchOf = (category?: string): Match | null => {
  const parsed = parseCategory(category);
  if (parsed.kind === "none") {
    return PERSONAL;
  }
  if (parsed.kind === "legacy") {
    const legacy: Record<string, Match> = {
      artist: ARTISTS,
      designer: ARTISTS,
      photographer: ARTISTS,
      influencer: CREATORS,
      infoproductor: COURSES,
      ecommerce: STORE,
      marketing: AGENCIES,
      social_projects: ORGANIZATIONS,
    };
    return legacy[parsed.key] ?? null;
  }
  const { segment, specialty } = parsed;
  switch (segment) {
    case "retail":
    case "fashion":
      return STORE;
    case "food":
      return specialty === "delivery" ? STORE : null;
    case "creators":
      if (specialty === "writing") return BLOGS;
      if (["photography", "design", "musician", "crafts"].includes(specialty)) return ARTISTS;
      return CREATORS;
    case "services":
      if (specialty === "tech") return TECH;
      if (specialty === "marketing") return AGENCIES;
      return PROFESSIONALS;
    case "health":
      return PROFESSIONALS;
    case "beauty":
      return STUDIOS;
    case "education":
      return ["infoproducts", "mentoring"].includes(specialty) ? COURSES : null;
    case "community":
      return ORGANIZATIONS;
    case "other":
      return PERSONAL;
    default:
      return null;
  }
};

/**
 * The extensions recommended to someone, from the fields of work of their Keepos (PLUS ones first: they're the
 * ones that can use a domain): extension → why. The first Keepo to recommend an extension gives the reason.
 */
export const recommendationsFor = (
  keepos: { category?: string; plus: boolean }[]
): Map<string, RecommendationReason> => {
  const ordered = [...keepos].sort((a, b) => Number(b.plus) - Number(a.plus));
  const result = new Map<string, RecommendationReason>();
  ordered.forEach(keepo => {
    const match = matchOf(keepo.category);
    match?.tlds.forEach(tld => {
      if (!result.has(tld)) {
        result.set(tld, match.reason);
      }
    });
  });
  return result;
};
